import { useRef, useState } from 'react';
import type { LibraryItem } from '@shared/types.ts';
import { Focusable } from '@/ui/Focusable.tsx';

/**
 * One tile. Shared by the home rows, the section grid and the episode list,
 * because three copies of this is three places for the progress bar to drift.
 */
export function Poster({
  item, onSelect, onOptions, autoFocus, wide,
}: {
  item: LibraryItem;
  onSelect(item: LibraryItem): void;
  /** Absent on screens with no menu to offer — the dots then never render. */
  onOptions?(item: LibraryItem): void;
  autoFocus?: boolean;
  /** 16:9 rather than 2:3 — episodes are stills, and stills are landscape. */
  wide?: boolean;
}) {
  /*
   * Some artwork genuinely does not exist. An episode falls back to its
   * season's thumbnail and then its series', and occasionally none of the
   * three is there — so the URL is well-formed, the server honestly answers
   * 404, and the browser draws a broken image icon. Falling back to the
   * initial is the difference between a gap and a mistake.
   */
  /*
   * THE WAY IN TO THE OPTIONS, for everyone who is not holding a remote.
   *
   * On a television the menu opens by HOLDING OK — see MainActivity. Neither
   * of the other two input devices can do that: a mouse has no long press, and
   * a finger's long press is claimed by the browser for text selection and the
   * context menu.
   *
   * So a mouse gets three dots, exactly where Plex puts them and where people
   * already look. A finger gets a real long press, implemented here because
   * the browser will not do it for us. A remote gets neither on screen, which
   * is right — there is no pointer to reveal them with and holding OK is the
   * gesture that works.
   */
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);

  const startHold = () => {
    held.current = false;
    holdTimer.current = setTimeout(() => {
      held.current = true;
      onOptions?.(item);
    }, 500);
  };
  const endHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };

  const [artBroken, setArtBroken] = useState(false);
  const showArt = item.images.primary && !artBroken;

  const pct = item.runtimeMs && item.userData.positionMs
    ? Math.min(100, Math.round((item.userData.positionMs / item.runtimeMs) * 100))
    : 0;

  return (
    <Focusable
      id={`lib-${item.id}`}
      className={`libe-card ${wide ? 'libe-wide' : ''}`}
      autoFocus={autoFocus}
      onSelect={() => {
        // A long press has already done something; the release must not also
        // start the film.
        if (held.current) { held.current = false; return; }
        onSelect(item);
      }}
      /* The room follows the cursor — see app/Backdrop.tsx. Declared as data
         rather than wired through a callback so nothing has to know. */
      backdrop={item.images.backdrop ?? item.images.primary ?? null}
    >
      <div
        className="libe-art"
        onPointerDown={(e) => { if (onOptions && e.pointerType === 'touch') startHold(); }}
        onPointerUp={endHold}
        onPointerCancel={endHold}
        onPointerLeave={endHold}
        /* A right-click is the mouse's own "what else can this do" and costs
           nothing to honour. */
        onContextMenu={(e) => {
          if (!onOptions) return;
          e.preventDefault();
          onOptions(item);
        }}
      >
        {onOptions && (
          <button
            type="button"
            className="libe-dots"
            aria-label={`Options for ${item.title}`}
            /* Not a Focusable: on a television this must never be a landing
               spot for the cursor — holding OK is that path, and a second stop
               on every tile would double the presses to cross a row. */
            tabIndex={-1}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onOptions(item); }}
          >
            ⋮
          </button>
        )}
        {showArt
          ? (
            <img
              src={item.images.primary}
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setArtBroken(true)}
            />
          )
          : <span className="libe-noart">{item.title.slice(0, 1)}</span>}

        {/* How far in they are, drawn on the poster rather than beside it —
            the one fact that decides whether this is the thing they wanted,
            and it has to read at a glance. */}
        {pct > 0 && (
          <div className="libe-progress" aria-label={`${pct}% watched`}>
            <span style={{ width: `${pct}%` }} />
          </div>
        )}

        {item.userData.played && pct === 0 && <div className="libe-seen" aria-label="Watched">✓</div>}

        {/* A season standing in for several new episodes says how many, or
            the tile is indistinguishable from any other season. */}
        {item.newEpisodeCount && item.newEpisodeCount > 1 && (
          <div className="libe-count">{item.newEpisodeCount} new</div>
        )}

        {/*
          The same overlay the search grid uses. A wall of posters answers
          "which one" and nothing else — under the cursor it should also say
          what the thing is, without costing a press.
        */}
        <div className="res-overlay">
          {item.year && <div className="res-ov-year">{item.year}</div>}
          <div className="res-ov-title">{item.title}</div>
          {item.overview && <p className="res-ov-text">{item.overview}</p>}
        </div>
      </div>

      {/*
        NO TITLE UNDER THE POSTER. It was there AND in the overlay, saying the
        same thing twice — and it was the thing making the rows ragged: a long
        title wraps to two lines, a short one does not, and a row of tiles
        ends up with its bottoms at four different heights.

        The poster is the identification. Words are for the one under the
        cursor, and the overlay says them — with the year and the synopsis,
        which the line underneath never could.
      */}
    </Focusable>
  );
}

/**
 * What pressing a tile means.
 *
 * A film plays. A series or a season OPENS, because "play" on a series is a
 * guess about which episode and the detail screen is where that guess gets
 * made visible. Continue Watching is the exception and passes `play` for
 * everything — resuming is the entire purpose of that row, and making
 * somebody walk through a series page to get back to the episode they were
 * halfway through would be the row failing at its one job.
 */
/**
 * Does pressing this OPEN it rather than play it?
 *
 * MOVIES OPEN NOW, and that is the correction. Pressing a film went straight
 * to playback, which is right in Continue Watching — you already chose it,
 * you are returning to it — and wrong everywhere else. In Recently Added, or
 * halfway down five thousand films, pressing one is how you ASK ABOUT it:
 * what is this, how long is it, who is in it. Answering with ninety minutes
 * of film starting at full volume is answering a different question, and the
 * only way back is BACK.
 *
 * Continue Watching is unaffected because it does not go through here — it
 * passes `play` directly, for exactly this reason.
 *
 * EPISODES STILL PLAY. Somebody inside a season, looking at a list of
 * episodes, has already answered every question a detail screen would. An
 * extra press there is friction in the commonest path in the app.
 */
export function isBrowsable(item: LibraryItem): boolean {
  return item.type === 'series' || item.type === 'season' || item.type === 'movie';
}
